import { useState, useContext, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useValidCode, useValidPassword } from "../hooks/useAuthHooks";
import Code from "../components/auth/code";
import Password from "../components/auth/password";
import { AuthContext } from "../context/authContext";
import { useLoading } from "../libs/loadingContext";
import AlertSignInError from "../components/alertSignInError";
import { useTranslation } from "react-i18next";

export default function ForgotPassword() {
  const [minutes, setMinutes] = useState(1);
  const [seconds, setSeconds] = useState(59);
  const { code, setCode, codeIsValid } = useValidCode("");
  const { password, setPassword, passwordIsValid } = useValidPassword("");
  const [error, setError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [verificationCode, setVerificationCode] = useState(false);
  const {
    password: passwordConfirm,
    setPassword: setPasswordConfirm,
    passwordIsValid: passwordConfirmIsValid,
  } = useValidPassword("");
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const { setLoading } = useLoading();
  const [searchParams] = useSearchParams();
  const isValidCode = !codeIsValid || code.length === 0;
  const isValidPassword = !passwordIsValid || password.length === 0;
  const { t } = useTranslation();

  const resetPassword = async () => {
    try {
      setLoading(true);
      if (password !== passwordConfirm) {
        setPasswordError(t("forgotPassword.passwordError"));
        return;
      }
      const username = localStorage.getItem("username");
      await authContext.forgotPassword(username, code, password);
      window.location.href = `${process.env.REACT_APP_AFTER_SIGNUP_URL}`;
    } catch (err) {
      if (
        err.message === "Invalid verification code provided, please try again."
      ) {
        setError(t("forgotPassword.confirmCodeError"));
        setVerificationCode(false);
      } else if (err instanceof Error) {
        setError(t("forgotPassword.confirmCodeError"));
        setVerificationCode(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = () => {
    setVerificationCode(true);
  };

  const handleRedirectUrl = () => {
    navigate("/request-code");
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1);
      }

      if (seconds === 0) {
        if (minutes === 0) {
          clearInterval(interval);
        } else {
          setSeconds(59);
          setMinutes(minutes - 1);
        }
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [seconds, minutes, verificationCode]);

  useEffect(() => {
    if (password === passwordConfirm) {
      setPasswordError("");
    }
  }, [password, passwordConfirm]);

  useEffect(() => {
    setMinutes(1);
    setSeconds(59);
  }, [verificationCode]);

  return (
    <>
      <div className="content-page">
        {verificationCode ? (
          <>
            <h3 className="process-title forget-password">
              {t("forgotPassword.forgotPasswordTitle")}
            </h3>
            <Password
              passwordIsValid={passwordIsValid}
              setPassword={setPassword}
              label={t("forgotPassword.password")}
              required={false}
              newPassword={true}
            />
            <Password
              passwordIsValid={passwordConfirmIsValid}
              setPassword={setPasswordConfirm}
              label={t("forgotPassword.passwordAgain")}
              required={false}
              newPassword={true}
            />
            {passwordError && (
              <div className="invalid-email">{passwordError}</div>
            )}
            <div className="forgot-password-request-btn">
              {isValidPassword ? (
                <button className="btn btn-default btn-default-disabled">
                  {t("forgotPassword.changePassword")}
                </button>
              ) : (
                <button
                  className="btn btn-default"
                  onClick={() => resetPassword()}
                >
                  {t("forgotPassword.changePassword")}
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <h3 className="process-title">
              {t("forgotPassword.confirmAccount")}
            </h3>
            <div className="process-desc">
              {t("forgotPassword.confirmAccountInfo")}
            </div>
            {error && <AlertSignInError message={error} />}
            <Code code={code} setCode={setCode} codeIsValid={codeIsValid} />
            <div className="forgot-password-continue">
              {isValidCode ? (
                <button className="btn btn-default btn-default-disabled">
                  {t("forgotPassword.continue")}
                </button>
              ) : (
                <button
                  className="btn btn-default"
                  onClick={() => handleVerification()}
                >
                  {t("forgotPassword.continue")}
                </button>
              )}
            </div>
            <div
              style={{ position: "inherit", marginTop: "40px" }}
              className="confirm-code"
            >
              <div>
                {t("forgotPassword.verificationCodeNotReceived")}{" "}
                {seconds > 0 || minutes > 0 ? (
                  <>
                    <span className="counter">
                      {minutes < 10 ? `0${minutes}` : minutes}:
                      {seconds < 10 ? `0${seconds}` : seconds}{" "}
                      {t("forgotPassword.youCanSendAgainWithinSeconds")}
                    </span>
                  </>
                ) : (
                  <>
                    <span onClick={handleRedirectUrl}>
                      {t("forgotPassword.sendAgain")}
                    </span>
                  </>
                )}
              </div>
              <div>
                {t("forgotPassword.differentNumber")}{" "}
                <span onClick={handleRedirectUrl}>
                  {t("forgotPassword.verify")}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
